import { MIRAKL_BEACON_PATHS } from '../constants';
import type { ClickBeaconBody, ViewBeaconBody } from '../types';

const sendBeacon = (apiBasePath: string, path: string, body: unknown): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
    ? navigator.sendBeacon(`${apiBasePath}${path}`, JSON.stringify(body))
    : false;

/**
 * Posts a `view` beacon with `navigator.sendBeacon` to `MIRAKL_BEACON_PATHS.view` under the calling
 * app's public API base path, so it reaches that app's own route. `sendBeacon` survives navigation
 * and ad blockers see no third-party host. Nothing in the browser talks to Mirakl. Returns what
 * `sendBeacon` returns, or false where it does not exist.
 *
 * The caller skips bots. Consent is decided by the route.
 *
 * @param apiBasePath the app's `PUBLIC_API_BASE_PATH`, with its trailing slash, e.g.
 * `/productdetails-api/`
 */
export const sendMiraklViewBeacon = (apiBasePath: string, body: ViewBeaconBody): boolean =>
  sendBeacon(apiBasePath, MIRAKL_BEACON_PATHS.view, body);

/**
 * Posts a `click` beacon to `MIRAKL_BEACON_PATHS.click`, as `sendMiraklViewBeacon` does. Call it
 * from every call to action on a sponsored tile, not only the link.
 */
export const sendMiraklClickBeacon = (apiBasePath: string, body: ClickBeaconBody): boolean =>
  sendBeacon(apiBasePath, MIRAKL_BEACON_PATHS.click, body);

/** OneTrust's advertising and targeting category, the one Mirakl views need. */
const TARGETING_GROUP = 'C0004';
/** The OneTrust script loads asynchronously; nobody can accept the banner before it has. */
const ONETRUST_POLL_MS = 250;
const ONETRUST_POLL_LIMIT_MS = 30_000;

interface OneTrustConsentEvent {
  /** The ids of the categories now accepted. */
  detail?: string[];
}

interface OneTrustSdk {
  OnConsentChanged(handler: (event?: OneTrustConsentEvent) => void): void;
}

/**
 * The categories now accepted: the event's `detail` when OneTrust passes one, otherwise the
 * `OnetrustActiveGroups` global it keeps up to date (`,C0001,C0004,`).
 */
const activeGroups = (event?: OneTrustConsentEvent): string[] =>
  event?.detail ??
  String((globalThis as { OnetrustActiveGroups?: string }).OnetrustActiveGroups ?? '')
    .split(',')
    .filter(Boolean);

/**
 * Calls `callback` once, when the visitor accepts advertising cookies in the OneTrust banner on
 * this page. A new visitor's first page renders before they have answered the banner, so its
 * server-side view is skipped; this lets the page send that view itself once consent exists.
 * A visitor who had already accepted gets no callback, even if they save their preferences again:
 * their view went from the server, and OneTrust raises its event on every save, not only a change.
 * Returns a function that cancels the subscription.
 */
export const onMiraklConsentGranted = (callback: () => void): (() => void) => {
  let active = true;
  let poll: ReturnType<typeof setInterval> | undefined;

  const handler = (event?: OneTrustConsentEvent) => {
    if (active && activeGroups(event).includes(TARGETING_GROUP)) {
      active = false;
      callback();
    }
  };
  const subscribe = (): boolean => {
    const oneTrust = (globalThis as { OneTrust?: OneTrustSdk }).OneTrust;
    if (typeof oneTrust?.OnConsentChanged !== 'function') {
      return false;
    }
    if (activeGroups().includes(TARGETING_GROUP)) {
      active = false;
      return true;
    }
    oneTrust.OnConsentChanged(handler);
    return true;
  };

  if (!subscribe()) {
    const started = Date.now();
    poll = setInterval(() => {
      if (!active || subscribe() || Date.now() - started > ONETRUST_POLL_LIMIT_MS) {
        clearInterval(poll);
      }
    }, ONETRUST_POLL_MS);
  }

  return () => {
    active = false;
    clearInterval(poll);
  };
};
